/* Guard native purchase forms while the existing Recharge widget binds them.
 * Reads widget state and Shopify allocations; never selects or writes a plan. */
function draftSubscriptionReadiness({ config, form, primary, widget }) {
  const fail = reason => ({ ready: false, reason });
  if (!form || !primary || !primary.isConnected) return fail('loading');
  const values = native => [...native.querySelectorAll('[name="selling_plan"]:not(:disabled)')].filter(input => !['radio', 'checkbox'].includes(input.type) || input.checked).map(input => String(input.value || ''));
  const variant = String(form.querySelector('[name="id"]:not(:disabled)')?.value || '');
  const primaryVariant = String(primary.querySelector('[name="id"]:not(:disabled)')?.value || '');
  const allocation = config.variants.find(item => String(item.id) === variant);
  if (!allocation || variant !== primaryVariant) return fail('variant');
  const submitted = values(form), bound = values(primary);
  // Two optional-plan multipacks have no storefront widget. Keep their native
  // forms usable without inventing a plan or discarding a selected recurrence.
  // A widget that appears later must pass the normal binding checks below.
  const nativeMultipack = config.allowNativeWithoutWidget === true &&
    ['8290516730029', '8396961874093'].includes(String(config.productId));
  if (!widget && nativeMultipack && !config.requiresPlan &&
      !submitted.some(Boolean) && !bound.some(Boolean)) return { ready: true, native: true };
  if (!widget || widget.state !== 'ready' || widget.productForm !== primary) return fail('loading');
  if (variant !== String(widget.variantId)) return fail('variant');
  if (bound.length !== 1) return fail('loading');
  if (widget.purchaseOption === 'onetime') {
    if (config.requiresPlan || widget.sellingPlanId || bound[0] || submitted.some(Boolean)) return fail('selection');
    return { ready: true };
  }
  if (!['subscription', 'prepaid_subscription'].includes(widget.purchaseOption)) return fail('selection');
  const selected = String(widget.sellingPlanId || '');
  if (!selected || !allocation.plans.map(String).includes(selected) || bound[0] !== selected) return fail('selection');
  if (submitted.length !== 1 || submitted[0] !== selected) return fail('form');
  return { ready: true };
}
(() => {
  if (window.__dsSubscriptionReadinessBooted) return;
  window.__dsSubscriptionReadinessBooted = true;
  const initialized = new WeakSet();
  function init(marker) {
    if (initialized.has(marker)) return;
    initialized.add(marker);
    let config;
    try { config = JSON.parse(marker.textContent); } catch { return; }
    const primary = () => document.getElementById(config.formId);
    const forms = () => [primary(), document.getElementById(config.stickyFormId)].filter(Boolean);
    const widget = () => document.querySelector(`recharge-subscription-widget[product-id="${config.productId}"]`);
    const notice = () => document.querySelector(`[data-ds-subscription-status="${config.formId}"]`);
    const owned = new WeakSet();
    let blockedForm = null;
    const state = form => {
      try { return draftSubscriptionReadiness({ config, form, primary: primary(), widget: widget() }); }
      catch { return { ready: false, reason: 'loading' }; }
    };
    function refresh() {
      const current = state(primary());
      const message = notice();
      if (message) {
        if (blockedForm && !forms().includes(blockedForm)) blockedForm = null;
        message.hidden = current.ready && (!blockedForm || (blockedForm === primary() && state(blockedForm).ready));
        if (!current.ready && widget()?.state === 'error') message.textContent = 'Purchase options are unavailable. Reload this page before adding.';
      }
      const sticky = document.getElementById(config.stickyFormId);
      const review = sticky?.querySelector('[data-ds-review-purchase-options]');
      const stickySubmit = sticky?.querySelector('[type="submit"]');
      if (review && stickySubmit) {
        // Recharge binds the main form; sticky is an explicit review handoff.
        const primarySubmit = primary()?.querySelector('[type="submit"]');
        // Sticky remains a review handoff. A stale disabled sticky submit must not
        // suppress it when the current main form is available after navigation.
        const fallback = !current.native && Boolean(primarySubmit) && !primarySubmit.disabled &&
          !primarySubmit.classList.contains('loading') && !stickySubmit.classList.contains('loading');
        review.hidden = !fallback;
        stickySubmit.hidden = fallback;
      }
      forms().forEach(form => form.querySelectorAll('[type="submit"],[name="add"]').forEach(button => {
        const allowed = state(form).ready;
        if (!allowed) {
          if (button.getAttribute('aria-disabled') !== 'true') {
            owned.add(button);
            button.setAttribute('aria-disabled', 'true');
          } else if (button.hasAttribute('data-ds-subscription-pending')) owned.add(button);
        } else if (owned.has(button) || button.hasAttribute('data-ds-subscription-pending')) {
          // Native in-flight or sold-out protections retain ownership.
          if (!button.classList.contains('loading') && !button.disabled) {
            button.removeAttribute('aria-disabled');
            owned.delete(button);
            button.removeAttribute('data-ds-subscription-pending');
          }
        }
      }));
    }
    function intercept(event) {
      const form = event.type === 'submit' ? event.target : event.target.closest('button[type="submit"],input[type="submit"],[name="add"],[data-ds-review-purchase-options]')?.form;
      if (!forms().includes(form)) return;
      const readiness = state(form);
      if (readiness.ready && (form === primary() || readiness.native)) { blockedForm = null; refresh(); return; }
      blockedForm = form;
      event.preventDefault(); event.stopImmediatePropagation();
      refresh();
      const message = notice();
      if (message) {
        message.hidden = false;
        message.textContent = widget()?.state === 'error' ? 'Purchase options are unavailable. Reload this page before adding.' : form !== primary() ? 'Review the purchase options above and use the main Add to cart button.' : 'Wait for the purchase options to finish loading before adding.';
        if (form !== primary()) {
          message.setAttribute('tabindex', '-1');
          message.scrollIntoView({ behavior: 'auto', block: 'center' });
          message.focus({ preventScroll: true });
        }
      }
    }
    window.addEventListener('click', intercept, true);
    window.addEventListener('submit', intercept, true);
    document.addEventListener('change', refresh);
    window.addEventListener('pageshow', refresh);
    window.addEventListener('popstate', refresh);
    document.addEventListener('afterVariantChanged', refresh);
    // Recharge's reactive state and hidden input values are properties, not
    // reflected attributes. Poll read-only state; no app callback is replaced.
    const timer = window.setInterval(() => { if (!marker.isConnected) { window.clearInterval(timer); window.removeEventListener('click', intercept, true); window.removeEventListener('submit', intercept, true); document.removeEventListener('change', refresh); window.removeEventListener('pageshow', refresh); window.removeEventListener('popstate', refresh); document.removeEventListener('afterVariantChanged', refresh); return; } refresh(); }, 100);
    refresh();
  }
  const boot = () => document.querySelectorAll('[data-ds-subscription-readiness]').forEach(init);
  boot();
  document.addEventListener('shopify:section:load', boot);
})();
